/**
 * Read a JWT's claims WITHOUT verifying it. The SPA never trusts a claim for
 * access — the backend verifies every token (D13, D20) — it only reads its own
 * token to show who is signed in and to know when to refresh.
 */
import { z } from 'zod';

const claimsSchema = z.object({
  sub: z.string().min(1),
  exp: z.number(),
  tenantId: z.string().min(1).optional(),
  'custom:tenantId': z.string().min(1).optional(),
  email: z.string().optional(),
});

export type TokenClaims = {
  readonly userId: string;
  /** The tenant claim (D5): `tenantId`, or Cognito's `custom:tenantId`; null when the token carries none. */
  readonly tenantId: string | null;
  readonly email: string | null;
  readonly expiresAtMs: number;
};

function base64UrlDecode(segment: string): string | null {
  try {
    const base64 = segment.replaceAll('-', '+').replaceAll('_', '/').padEnd(Math.ceil(segment.length / 4) * 4, '=');
    const binary = atob(base64);
    return new TextDecoder().decode(Uint8Array.from(binary, (c) => c.charCodeAt(0)));
  } catch {
    return null;
  }
}

/** The claims the SPA reads, or null for anything that is not a JWT carrying `sub` and `exp`. */
export function readTokenClaims(token: string): TokenClaims | null {
  const payload = token.split('.')[1];
  if (payload === undefined) return null;
  const text = base64UrlDecode(payload);
  if (text === null) return null;
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch {
    return null;
  }
  const parsed = claimsSchema.safeParse(json);
  if (!parsed.success) return null;
  const c = parsed.data;
  return { userId: c.sub, tenantId: c.tenantId ?? c['custom:tenantId'] ?? null, email: c.email ?? null, expiresAtMs: c.exp * 1000 };
}
